





























































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import MaterialCard from '@/components/material/Card/index.vue';
import {
  IReportSummaryData,
  IssuingRecordActionEnum,
  IDate,
  IExternalAccount,
} from '@/api/types';
import {
  IReportRequest,
  IGetIssueRecordReportResponse,
  IReportRequestSourceIdHolder,
  getIssuingReport,
} from '@/api/report';
import { AppModule } from '@/store/modules/app';
import {
  translateViewResource,
  stringToIDate,
  formatApiError,
} from '@/utils/index';
import {
  sortByPlanAndUnit,
  planAndUnitWithBookingTypeToText,
  PlanAndUnitWithBookingType,
} from '@/utils/tickets';

type ReportRowByPlanUnit = {
  accountId: string;
  accountName: string;
  issueCount: number;
  revokeCount: number;
  refundCount: number;
  rechargeCount: number;
  adminAdjustCount: number;
  totalCount: number;
};

type ReportTableByPlanUnit = {
  key: PlanAndUnitWithBookingType;
  table: ReportRowByPlanUnit[];
};

@Component({
  name: 'ReportSearchResult',
  components: {
    MaterialCard,
  },
})
export default class extends Vue {
  @Prop({ required: false }) issuingDateRange?: string;
  @Prop({ required: false }) participationDateRange?: string;
  @Prop({ required: false }) sourceIds?: string;
  @Prop({ required: false }) accountIds?: string;

  private reports: IReportSummaryData[] = [];
  private reportHeaders = [
    { text: 'Account Id', value: 'accountId' },
    { text: 'Account name', value: 'accountName' },
    { text: 'Activity Id', value: 'activityId' },
    { text: 'Activity title', value: 'activityTitle' },
    { text: 'Plan Id', value: 'planId' },
    { text: 'Plan title', value: 'planTitle' },
    { text: 'Unit Id', value: 'unitId' },
    { text: 'Unit title', value: 'unitTitle' },
    { text: 'Action Id', value: 'actionId' },
    { text: 'Action title', value: 'actionTitle' },
    { text: 'Amount', value: 'amount' },
  ];
  private reportsByPlanUnit: ReportTableByPlanUnit[] = [];
  private searchConditions: { key: string; value: string }[] = [];
  private search = '';
  private loading = true;

  get translatedReportByPlanUnitHeaders() {
    return [
      { text: this.$t('Account name'), value: 'accountName' },
      { text: this.translateViewResource('TOTAL count'), value: 'totalCount' },
      { text: this.translateViewResource('ISSUE count'), value: 'issueCount' },
      {
        text: this.translateViewResource('REVOKE count'),
        value: 'revokeCount',
      },
      {
        text: this.translateViewResource('REFUND count'),
        value: 'refundCount',
      },
      {
        text: this.translateViewResource('RECHARGE count'),
        value: 'rechargeCount',
      },
      {
        text: this.translateViewResource('ADMIN_ADJUST count'),
        value: 'adminAdjustCount',
      },
    ];
  }

  mounted() {
    this.fetchData();
  }

  private async fetchData() {
    AppModule.StartProgress();
    this.loading = true;
    const req = {} as IReportRequest;
    let searchConditions: { key: string; value: string }[] = [];

    let issuingDateRange: string[] = [];
    if (this.issuingDateRange) {
      issuingDateRange = JSON.parse(this.issuingDateRange) as string[];
    }
    if (issuingDateRange && issuingDateRange.length == 2) {
      req.issuingDateRange = {
        start: stringToIDate(issuingDateRange[0]) as IDate,
        end: stringToIDate(issuingDateRange[1]) as IDate,
      };
      searchConditions.push({
        key: 'Issuing date',
        value: `${issuingDateRange[0]} ~ ${issuingDateRange[1]}`,
      });
    }

    let participationDateRange: string[] = [];
    if (this.participationDateRange) {
      participationDateRange = JSON.parse(
        this.participationDateRange
      ) as string[];
    }
    if (participationDateRange && participationDateRange.length == 2) {
      req.participationDateRange = {
        start: stringToIDate(participationDateRange[0]) as IDate,
        end: stringToIDate(participationDateRange[1]) as IDate,
      };
      searchConditions.push({
        key: 'Participation date',
        value: `${participationDateRange[0]} ~ ${participationDateRange[1]}`,
      });
    }

    let sourceIds: IReportRequestSourceIdHolder[] = [];
    let planAndUnitItems: PlanAndUnitWithBookingType[] = [];
    if (this.sourceIds) {
      planAndUnitItems = JSON.parse(
        this.sourceIds
      ) as PlanAndUnitWithBookingType[];
      planAndUnitItems.forEach(item => {
        sourceIds.push({
          planId: item.planId,
          unitId: item.unitId,
        } as IReportRequestSourceIdHolder);
      });
    }
    if (sourceIds && sourceIds.length > 0) {
      req.sourceIds = sourceIds;
      searchConditions.push({
        key: 'Source Ids',
        value: `[${planAndUnitItems
          .map(
            item => `"${planAndUnitWithBookingTypeToText(this.$i18n, item)}"`
          )
          .join(', ')}]`,
      });
    }

    let accountIds: IExternalAccount[] = [];
    if (this.accountIds) {
      accountIds = JSON.parse(this.accountIds) as IExternalAccount[];
    }
    if (accountIds && accountIds.length > 0) {
      req.accountIds = accountIds.map(a => a.id);
      searchConditions.push({
        key: 'Account name',
        value: JSON.stringify(accountIds.map(a => a.fullName)),
      });
    }

    try {
      const res = await getIssuingReport(req);
      this.reports = res.summaries;
      this.reportsByPlanUnit = this.generateReportsByPlanUnit(res.summaries);
      this.searchConditions = searchConditions;

      this.loading = false;
      AppModule.StopProgress();
    } catch (err) {
      this.loading = false;
      AppModule.StopProgress();
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t(`@route.Report search`) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private generateReportsByPlanUnit(
    reports: IReportSummaryData[]
  ): ReportTableByPlanUnit[] {
    const reduced = reports.reduce(
      (
        map: {
          [id: string]: {
            key: PlanAndUnitWithBookingType;
            rowByAccount: { [accountId: string]: ReportRowByPlanUnit };
          };
        },
        obj: IReportSummaryData
      ) => {
        const id = `${obj.planId}#${obj.unitId}#${obj.bookingType}`;
        map[id] = map[id] || {
          key: {
            activityId: obj.activityId,
            activityTitle: obj.activityTitle,
            planId: obj.planId,
            planTitle: obj.planTitle,
            unitId: obj.unitId,
            unitTitle: obj.unitTitle,
            bookingType: obj.bookingType,
          },
          rowByAccount: {},
        };
        map[id].rowByAccount[obj.accountId] = map[id].rowByAccount[
          obj.accountId
        ] || {
          accountId: obj.accountId,
          accountName: obj.accountName,
          issueCount: 0,
          revokeCount: 0,
          refundCount: 0,
          rechargeCount: 0,
          adminAdjustCount: 0,
          totalCount: 0,
        };
        switch (obj.actionId) {
          case IssuingRecordActionEnum.ISSUE:
            map[id].rowByAccount[obj.accountId].issueCount += obj.amount;
            break;
          case IssuingRecordActionEnum.REVOKE:
            map[id].rowByAccount[obj.accountId].revokeCount += obj.amount;
            break;
          case IssuingRecordActionEnum.REFUND:
            map[id].rowByAccount[obj.accountId].refundCount += obj.amount;
            break;
          case IssuingRecordActionEnum.RECHARGE:
            map[id].rowByAccount[obj.accountId].rechargeCount += obj.amount;
            break;
          case IssuingRecordActionEnum.ADMIN_ADJUST:
            map[id].rowByAccount[obj.accountId].adminAdjustCount += obj.amount;
            break;
          default:
            break;
        }
        map[id].rowByAccount[obj.accountId].totalCount += obj.amount;
        return map;
      },
      {} as {
        [id: string]: {
          key: PlanAndUnitWithBookingType;
          rowByAccount: { [accountId: string]: ReportRowByPlanUnit };
        };
      }
    );

    return Object.values(reduced)
      .map(value => {
        return {
          key: value.key,
          table: Object.values(value.rowByAccount).sort((a, b) => {
            return a.accountId < b.accountId
              ? -1
              : a.accountId > b.accountId
              ? 1
              : 0;
          }),
        } as ReportTableByPlanUnit;
      })
      .sort((a, b) => sortByPlanAndUnit(a.key, b.key));
  }

  private getPlanUnitCaption(planAndUnit: PlanAndUnitWithBookingType) {
    return planAndUnitWithBookingTypeToText(this.$i18n, planAndUnit);
  }

  private translateViewResource(resource: string) {
    return translateViewResource(
      this.$i18n,
      this.$options.name as string,
      resource
    );
  }
}
